import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import Image from '@segment/matcha/components/Image'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import { SignupButtonText } from '@segment/matcha/recipes/SignupButton/SignupButton'

import DemoForm from '@segment/templates/Forms/DemoForm'

import heroXsPng2x from './assets/Hero-xs@2x.png'
import heroXsWebp from './assets/Hero-xs.webp'
import heroXsWebp2x from './assets/Hero-xs@2x.webp'

import heroMdPng from './assets/Hero-md.png'
import heroMdPng2x from './assets/Hero-md@2x.png'
import heroMdWebp from './assets/Hero-md.webp'
import heroMdWebp2x from './assets/Hero-md@2x.webp'

const copy = {
  overline: `Protocols`,
  heading: `Good data, always`,
  description: `Take control of your data quality with intuitive data cleaning features and real-time validation built for enterprise scale.`,
  buttonText: `Get a demo`,
  linkText: `Create a free account`,
}

const Hero: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()

  const afterContent = (
    <SignupButtonText size="medium" tag="a" iconEnd={ChevronRightIcon}>
      {copy.linkText}
    </SignupButtonText>
  )

  return (
    <Wrapper>
      <HeroComponent variant="centered">
        <HeroCopy>
          <CopyTextBase centered={true}>
            <CopyText.Overline variant="h5">{copy.overline}</CopyText.Overline>
            <CopyText.Heading variant="h1" tag="h1">
              {copy.heading}
            </CopyText.Heading>
            <StyledCopyTextDescription variant="pExtraLarge" tag="h2">
              {copy.description}
            </StyledCopyTextDescription>
          </CopyTextBase>
          <HeroRecipe.Form>
            <DemoForm
              data-hook="demoFormContainer"
              isProgressive={true}
              customFormIdentifier="site-protocols-demo"
              successMessageAppearance="twilioLight"
              after={afterContent}
            />
          </HeroRecipe.Form>
        </HeroCopy>
        <Picture>
          <source
            type="image/webp"
            media={theme.breakpoints.up(`lg`, false)}
            srcSet={`${heroMdWebp}, ${heroMdWebp2x} 2x`}
          />
          <source media={theme.breakpoints.up(`lg`, false)} srcSet={`${heroMdPng}, ${heroMdPng2x} 2x`} />
          <source type="image/webp" srcSet={`${heroXsWebp}, ${heroXsWebp2x} 2x`} />
          <StyledImage
            src={heroXsPng2x}
            alt="Illustration: Complete data collected across the entire customer journey"
            width={1218}
            height={584}
            loading="lazy"
          />
        </Picture>
      </HeroComponent>
    </Wrapper>
  )
}

const Picture = styled.picture`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    position: relative;
    top: -100px;
    margin-bottom: -100px;
    pointer-events: none;
  }
`

const Wrapper = styled.header`
  position: relative;
  z-index: 0;
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const HeroComponent = styled(HeroBase)`
  max-width: 100%;

  ${HeroRecipe.Wrapper} {
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      row-gap: 20px;
    }
  }
`

const HeroCopy = styled(HeroRecipe.Copy)`
  max-width: 844px;
`

const StyledCopyTextDescription = styled(CopyText.Description)`
  max-width: 650px;
`

const StyledImage = styled(Image)`
  padding-bottom: 0;
`

export default Hero
